import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { DataCard, DataCardActions, DataCardEmpty, DataCardRow } from '../../components/ui/DataCard';
import { TextField } from '../../components/forms/TextField';
import { CurrencyInput } from '../../components/forms/CurrencyInput';
import { ImageUpload, type PendingFile } from '../../components/forms/ImageUpload';
import { cn } from '../../utils/cn';
import { formatPrice } from '../../utils/formatPrice';
import { bookingApi, type ServiceEntity, type ServiceTypeEntity } from '../../api/BookingApi';
import { filesApi } from '../../api/FilesApi';
import { RichTextEditor } from '../../components/forms/RichTextEditor';
import { useI18n } from '../../i18n/I18nContext';

function Badge({ children, tone }: { children: React.ReactNode; tone: 'green' | 'amber' | 'slate' }) {
  const tones: Record<typeof tone, string> = {
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tones[tone])}>
      {children}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{children}</div>
      <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

/* ── Drag handle icon ── */
function DragHandle() {
  return (
    <svg className="h-5 w-5 text-slate-400 dark:text-slate-500" viewBox="0 0 20 20" fill="currentColor">
      <path d="M7 2a2 2 0 10.001 4.001A2 2 0 007 2zm0 6a2 2 0 10.001 4.001A2 2 0 007 8zm0 6a2 2 0 10.001 4.001A2 2 0 007 14zm6-8a2 2 0 10-.001-4.001A2 2 0 0013 6zm0 2a2 2 0 10.001 4.001A2 2 0 0013 8zm0 6a2 2 0 10.001 4.001A2 2 0 0013 14z" />
    </svg>
  );
}

/* ── Sortable row component ── */
type ReorderItem = { serviceId: string; title: string; code: string; sequence: number };

function SortableRow({ item, index, typeName }: { item: ReorderItem; index: number; typeName?: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.serviceId,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 rounded-lg border bg-white px-4 py-3 transition-shadow dark:bg-slate-900',
        isDragging ? 'z-50 border-red-300 shadow-lg ring-2 ring-red-200 dark:border-red-700 dark:ring-red-900/40' : 'border-slate-200 dark:border-slate-700',
      )}
    >
      <button type="button" className="cursor-grab touch-none" {...attributes} {...listeners}>
        <DragHandle />
      </button>
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-slate-900 dark:text-slate-100">{item.title}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{item.code}{typeName ? ` · ${typeName}` : ''}</div>
      </div>
    </div>
  );
}

type ServiceDraft = {
  id?: string;
  title: string;
  code: string;
  serviceTypeId: string;
  price: string;
  currency: string;
  unitValue: string;
  description: string;
  remark: string;
  commissionPercentage: string;
  status: string;
  image: string;
  imageUrl: string;
  allowAttendedBy: string[];
  allowExternalBooking: boolean;
  isSubscription: boolean;
  subscriptionLimit: string;
  subscriptionDuration: string;
  validFrom: string;
  validTo: string;
  sequence: string;
};

const EMPTY_DRAFT: ServiceDraft = {
  title: '',
  code: '',
  serviceTypeId: '',
  price: '',
  currency: 'IDR',
  unitValue: '',
  description: '',
  remark: '',
  commissionPercentage: '',
  status: 'active',
  image: '',
  imageUrl: '',
  allowAttendedBy: [],
  allowExternalBooking: false,
  isSubscription: false,
  subscriptionLimit: '',
  subscriptionDuration: '',
  validFrom: '',
  validTo: '',
  sequence: '',
};

const DEBOUNCE_MS = 300;

const statusTone = (status: string): 'green' | 'amber' | 'slate' => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'green';
    case 'inactive':
      return 'amber';
    default:
      return 'slate';
  }
};

/** Converts snake_case enum values (e.g. "no_show") into camelCase i18n keys (e.g. "noShow"). */
function toCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function formatDateForInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

const UNCATEGORIZED = '__uncategorized__';

export function ServicesPage() {
  const { t } = useI18n();
  const [allServices, setAllServices] = useState<ServiceEntity[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeEntity[]>([]);
  const [attendedByUsers, setAttendedByUsers] = useState<{ userId: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [draft, setDraft] = useState<ServiceDraft>({ ...EMPTY_DRAFT });
  const [pendingImage, setPendingImage] = useState<PendingFile | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Reorder mode state ── */
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderTypeFilter, setReorderTypeFilter] = useState('');
  const [reorderItems, setReorderItems] = useState<ReorderItem[]>([]);
  const [reorderOriginal, setReorderOriginal] = useState<ReorderItem[]>([]);
  const [reorderSaving, setReorderSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const [services, types, attended] = await Promise.all([
        bookingApi.listServices(),
        bookingApi.listServiceTypes(),
        bookingApi.listAttendedBy(),
      ]);
      setAllServices(services.data);
      setServiceTypes(types.data);
      setAttendedByUsers(attended.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('services.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const filtered = useMemo(() => {
    let result = allServices;
    if (filterType) {
      result = result.filter((s) => s.serviceTypeId === filterType);
    }
    if (debouncedQuery) {
      const q = debouncedQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q)
      );
    }
    return result.slice().sort((a, b) => {
      const sa = a.sequence ?? Number.MAX_SAFE_INTEGER;
      const sb = b.sequence ?? Number.MAX_SAFE_INTEGER;
      return sa - sb;
    });
  }, [allServices, debouncedQuery, filterType]);

  const total = filtered.length;
  const page = useMemo(() => filtered.slice(skip, skip + limit), [filtered, skip, limit]);

  function handleSearchChange(value: string) {
    setQuery(value);
    setSkip(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, DEBOUNCE_MS);
  }

  function handlePageChange(next: { skip: number; limit: number }) {
    setSkip(next.skip);
    setLimit(next.limit);
  }

  function refresh() {
    loadServices();
  }

  const typeMap = useMemo(() => {
    const map = new Map<string, string>();
    serviceTypes.forEach((st) => map.set(st.serviceTypeId, st.type));
    return map;
  }, [serviceTypes]);

  function openCreate() {
    setError(null);
    setDraft({ ...EMPTY_DRAFT });
    setPendingImage(null);
    setModalOpen(true);
  }

  function openEdit(svc: ServiceEntity) {
    setError(null);
    setPendingImage(null);
    setDraft({
      id: svc.serviceId,
      title: svc.title,
      code: svc.code,
      serviceTypeId: svc.serviceTypeId ?? '',
      price: String(svc.price),
      currency: svc.currency || 'IDR',
      unitValue: svc.unitValue ?? '',
      description: svc.description ?? '',
      remark: svc.remark ?? '',
      commissionPercentage: svc.commissionPercentage != null ? String(svc.commissionPercentage) : '',
      status: svc.status,
      image: svc.image ?? '',
      imageUrl: svc.imageFile?.s3Url ?? '',
      allowAttendedBy: svc.allowAttendedBy ?? [],
      allowExternalBooking: svc.allowExternalBooking ?? false,
      isSubscription: svc.isSubscription ?? false,
      subscriptionLimit: svc.subscriptionLimit != null ? String(svc.subscriptionLimit) : '',
      subscriptionDuration: svc.subscriptionDuration != null ? String(svc.subscriptionDuration) : '',
      validFrom: formatDateForInput(svc.validFrom),
      validTo: formatDateForInput(svc.validTo),
      sequence: svc.sequence != null ? String(svc.sequence) : '',
    });
    setModalOpen(true);
  }

  async function saveDraft() {
    setError(null);
    const title = draft.title.trim();
    const code = draft.code.trim();
    if (!title) return setError(t('services.validation.titleRequired'));
    if (!code) return setError(t('services.validation.codeRequired'));

    const price = parseFloat(draft.price);
    if (isNaN(price) || price < 0) return setError(t('services.validation.priceInvalid'));

    setSaving(true);
    try {
      let imageId = draft.image;
      if (pendingImage) {
        const { data: fileObj } = await filesApi.upload(pendingImage.name, pendingImage.base64);
        imageId = fileObj.fileId;
      }

      const selectedType = draft.serviceTypeId ? typeMap.get(draft.serviceTypeId) : undefined;
      const payload: Partial<ServiceEntity> = {
        title,
        code,
        serviceTypeId: draft.serviceTypeId || undefined,
        serviceType: selectedType,
        price,
        currency: draft.currency || 'IDR',
        unitValue: draft.unitValue || undefined,
        description: draft.description.trim() || undefined,
        remark: draft.remark.trim() || undefined,
        commissionPercentage: draft.commissionPercentage ? parseFloat(draft.commissionPercentage) : undefined,
        status: draft.status,
        image: imageId || undefined,
        allowAttendedBy: draft.allowAttendedBy.length > 0 ? draft.allowAttendedBy : undefined,
        allowExternalBooking: draft.allowExternalBooking,
        isSubscription: draft.isSubscription,
        subscriptionLimit: draft.isSubscription && draft.subscriptionLimit ? Number(draft.subscriptionLimit) : undefined,
        subscriptionDuration: draft.isSubscription && draft.subscriptionDuration ? Number(draft.subscriptionDuration) : undefined,
        validFrom: draft.validFrom || undefined,
        validTo: draft.validTo || undefined,
        sequence: draft.sequence ? Number(draft.sequence) : undefined,
      };

      if (draft.id) {
        await bookingApi.updateService(draft.id, payload);
      } else {
        await bookingApi.createService(payload);
      }
      setModalOpen(false);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('services.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function duplicateService(svc: ServiceEntity) {
    const ok = window.confirm(t('services.confirmDuplicate', { title: svc.title }));
    if (!ok) return;
    try {
      await bookingApi.duplicateService(svc.serviceId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('services.duplicateFailed'));
    }
  }

  async function removeService(svc: ServiceEntity) {
    const ok = window.confirm(t('services.confirmDelete', { title: svc.title }));
    if (!ok) return;
    try {
      await bookingApi.deleteService(svc.serviceId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('services.deleteFailed'));
    }
  }

  function toggleAttendedBy(userId: string) {
    setDraft((d) => {
      const next = d.allowAttendedBy.includes(userId)
        ? d.allowAttendedBy.filter((id) => id !== userId)
        : [...d.allowAttendedBy, userId];
      return { ...d, allowAttendedBy: next };
    });
  }

  /* ── Reorder mode logic ── */
  function enterReorderMode() {
    const initialFilter = serviceTypes.length > 0 ? serviceTypes[0].serviceTypeId : UNCATEGORIZED;
    setReorderTypeFilter(initialFilter);
    buildReorderItems(initialFilter);
    setReorderMode(true);
  }

  function buildReorderItems(typeId: string) {
    const subset = allServices
      .filter((s) => (typeId === UNCATEGORIZED ? !s.serviceTypeId : s.serviceTypeId === typeId))
      .slice()
      .sort((a, b) => (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER))
      .map((s) => ({ serviceId: s.serviceId, title: s.title, code: s.code, sequence: s.sequence ?? 0 }));
    setReorderItems(subset);
    setReorderOriginal(subset.map((i) => ({ ...i })));
  }

  function handleReorderTypeChange(typeId: string) {
    setReorderTypeFilter(typeId);
    buildReorderItems(typeId);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setReorderItems((items) => {
      const oldIndex = items.findIndex((i) => i.serviceId === active.id);
      const newIndex = items.findIndex((i) => i.serviceId === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  }

  const reorderDirty = useMemo(() => {
    if (reorderItems.length !== reorderOriginal.length) return true;
    return reorderItems.some((item, i) => item.serviceId !== reorderOriginal[i]?.serviceId);
  }, [reorderItems, reorderOriginal]);

  async function saveReorder() {
    setReorderSaving(true);
    try {
      const updates: Promise<unknown>[] = [];
      for (let i = 0; i < reorderItems.length; i++) {
        const item = reorderItems[i];
        const origIdx = reorderOriginal.findIndex((o) => o.serviceId === item.serviceId);
        if (origIdx !== i) {
          updates.push(bookingApi.updateServiceSequence(item.serviceId, i));
        }
      }
      if (updates.length > 0) {
        await Promise.all(updates);
        showToast(t('services.savedOrderFor', { count: updates.length }));
      }
      setReorderMode(false);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('services.saveOrderFailed'));
    } finally {
      setReorderSaving(false);
    }
  }

  function cancelReorder() {
    setReorderMode(false);
  }

  const selectClass =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

  /* ── Reorder mode view ── */
  if (reorderMode) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('services.reorderTitle')}</div>
            <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {t('services.reorderSubtitle')}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
            <Button variant="secondary" onClick={cancelReorder} disabled={reorderSaving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={saveReorder} disabled={reorderSaving || !reorderDirty}>
              {reorderSaving ? t('common.saving') : t('services.saveOrder')}
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        {toast && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
            {toast}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-4">
            <label className="block w-full sm:w-64">
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('services.category')}</div>
              <select
                className={selectClass}
                value={reorderTypeFilter}
                onChange={(e) => handleReorderTypeChange(e.target.value)}
                disabled={reorderSaving}
              >
                {serviceTypes.map((st) => (
                  <option key={st.serviceTypeId} value={st.serviceTypeId}>
                    {st.type}
                  </option>
                ))}
                <option value={UNCATEGORIZED}>{t('services.uncategorized')}</option>
              </select>
            </label>
          </div>

          {reorderItems.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('services.noServicesInCategory')}
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={reorderItems.map((i) => i.serviceId)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {reorderItems.map((item, idx) => (
                    <SortableRow
                      key={item.serviceId}
                      item={item}
                      index={idx}
                      typeName={reorderTypeFilter !== UNCATEGORIZED ? undefined : undefined}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {reorderDirty && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              {t('services.unsavedChanges')}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Normal table view ── */
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('services.title')}</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {t('services.subtitle')}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button variant="secondary" onClick={enterReorderMode} disabled={loading || allServices.length === 0}>
            {t('services.reorder')}
          </Button>
          <Button variant="secondary" onClick={refresh} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button onClick={openCreate}>{t('services.addService')}</Button>
        </div>
      </div>

      {toast && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
          {toast}
        </div>
      )}

      {error && !modalOpen ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="w-full sm:w-64">
              <TextField
                label={t('common.search')}
                placeholder={t('services.searchPlaceholder')}
                value={query}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
            <label className="block w-full sm:w-48">
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('services.category')}</div>
              <select
                className={selectClass}
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value); setSkip(0); }}
              >
                <option value="">{t('services.allCategories')}</option>
                {serviceTypes.map((type) => (
                  <option key={type.serviceTypeId} value={type.serviceTypeId}>
                    {type.type}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t('common.total')} <span className="font-semibold text-slate-700 dark:text-slate-300">{total}</span>
          </div>
        </div>

        {/* Mobile card list */}
        <div className="mt-4 space-y-3 md:hidden">
          {loading && page.length === 0 ? (
            <DataCardEmpty>{t('services.loadingServices')}</DataCardEmpty>
          ) : page.length === 0 ? (
            <DataCardEmpty>{t('common.noResults')}</DataCardEmpty>
          ) : (
            page.map((s) => (
              <DataCard key={s.serviceId}>
                <div className="flex items-start gap-3">
                  {s.imageFile?.s3Url ? (
                    <img src={s.imageFile.s3Url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                  ) : (
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {s.sequence ?? '—'}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-slate-900 dark:text-slate-100">{s.title}</div>
                    <div className="truncate font-mono text-xs text-slate-500 dark:text-slate-400">{s.code}</div>
                  </div>
                  <Badge tone={statusTone(s.status)}>
                    {t(`common.status.${toCamel(s.status.toLowerCase())}`)}
                  </Badge>
                </div>
                <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                  <DataCardRow label={t('services.table.type')}>
                    {s.serviceType || (s.serviceTypeId && typeMap.get(s.serviceTypeId)) || '—'}
                  </DataCardRow>
                  <DataCardRow label={t('services.table.price')}>
                    {formatPrice(s.price, s.currency)}
                  </DataCardRow>
                  <DataCardRow label={t('services.table.duration')}>
                    {s.unitValue ? t('services.minutesSuffix', { value: s.unitValue }) : '—'}
                  </DataCardRow>
                  <DataCardRow label={t('services.table.position')}>{s.sequence ?? '—'}</DataCardRow>
                </div>
                <DataCardActions>
                  <Button variant="secondary" size="sm" onClick={() => openEdit(s)}>
                    {t('common.edit')}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => duplicateService(s)}>
                    {t('common.duplicate')}
                  </Button>
                  {s.status.toLowerCase() === 'active' && (
                    <Button variant="danger" size="sm" onClick={() => removeService(s)}>
                      {t('common.delete')}
                    </Button>
                  )}
                </DataCardActions>
              </DataCard>
            ))
          )}
        </div>

        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500 dark:text-slate-400">
              <tr>
                <th className="w-12 py-2 pr-2 font-semibold">{t('services.table.position')}</th>
                <th className="py-2 pr-4 font-semibold">{t('services.table.title')}</th>
                <th className="py-2 pr-4 font-semibold">{t('services.table.code')}</th>
                <th className="py-2 pr-4 font-semibold">{t('services.table.type')}</th>
                <th className="py-2 pr-4 font-semibold">{t('services.table.price')}</th>
                <th className="py-2 pr-4 font-semibold">{t('services.table.duration')}</th>
                <th className="py-2 pr-4 font-semibold">{t('services.table.status')}</th>
                <th className="py-2 pr-0 font-semibold text-right">{t('services.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && page.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={8}>
                    {t('services.loadingServices')}
                  </td>
                </tr>
              ) : page.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={8}>
                    {t('common.noResults')}
                  </td>
                </tr>
              ) : (
                page.map((s) => (
                  <tr key={s.serviceId} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="py-3 pr-2 text-center">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {s.sequence ?? '—'}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        {s.imageFile?.s3Url && (
                          <img src={s.imageFile.s3Url} alt="" className="h-8 w-8 rounded-lg object-cover" />
                        )}
                        <div className="font-medium text-slate-900 dark:text-slate-100">{s.title}</div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs text-slate-600 dark:text-slate-400">{s.code}</td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">
                      {s.serviceType || (s.serviceTypeId && typeMap.get(s.serviceTypeId)) || '—'}
                    </td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{formatPrice(s.price, s.currency)}</td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">
                      {s.unitValue ? t('services.minutesSuffix', { value: s.unitValue }) : '—'}
                    </td>
                    <td className="py-3 pr-4">
                      <Badge tone={statusTone(s.status)}>{t(`common.status.${toCamel(s.status.toLowerCase())}`)}</Badge>
                    </td>
                    <td className="py-3 pr-0">
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => openEdit(s)}>
                          {t('common.edit')}
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => duplicateService(s)}>
                          {t('common.duplicate')}
                        </Button>
                        {s.status.toLowerCase() === 'active' && (
                          <Button variant="danger" size="sm" onClick={() => removeService(s)}>
                            {t('common.delete')}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Pagination
            total={total}
            limit={limit}
            skip={skip}
            onChange={handlePageChange}
          />
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={draft.id ? t('services.editService') : t('services.addService')}
        onClose={() => (saving ? null : setModalOpen(false))}
        size="lg"
      >
        <div className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          ) : null}

          {/* ── Basic Info ── */}
          <SectionLabel>{t('services.sections.basicInfo')}</SectionLabel>

          <ImageUpload
            label={t('services.fields.image')}
            currentUrl={draft.imageUrl}
            pendingFile={pendingImage}
            onSelect={setPendingImage}
            onClear={() => setDraft((d) => ({ ...d, image: '', imageUrl: '' }))}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('services.fields.title')}
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder={t('services.fields.titlePlaceholder')}
            />
            <TextField
              label={t('services.fields.code')}
              value={draft.code}
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
              placeholder={t('services.fields.codePlaceholder')}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('services.fields.serviceType')}</div>
              <select
                className={selectClass}
                value={draft.serviceTypeId}
                onChange={(e) => setDraft((d) => ({ ...d, serviceTypeId: e.target.value }))}
              >
                <option value="">{t('services.fields.noneOption')}</option>
                {serviceTypes.map((st) => (
                  <option key={st.serviceTypeId} value={st.serviceTypeId}>
                    {st.type}
                  </option>
                ))}
              </select>
            </label>
            <div>
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('services.fields.status')}</div>
              <div className="flex flex-wrap gap-2">
                {['active', 'inactive', 'hidden'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, status: opt }))}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition capitalize',
                      draft.status === opt
                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                    )}
                  >
                    {t(`common.status.${toCamel(opt)}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <TextField
            label={t('services.fields.position')}
            type="number"
            min="0"
            value={draft.sequence}
            onChange={(e) => setDraft((d) => ({ ...d, sequence: e.target.value }))}
            placeholder={t('services.fields.positionPlaceholder')}
            hint={t('services.fields.positionHint')}
          />

          {/* ── Pricing ── */}
          <SectionLabel>{t('services.sections.pricing')}</SectionLabel>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CurrencyInput
              label={t('services.fields.price')}
              value={draft.price}
              onChange={(val) => setDraft((d) => ({ ...d, price: val }))}
              currency={draft.currency}
            />
            <div>
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('services.fields.currency')}</div>
              <div className="flex flex-wrap gap-2">
                {['IDR', 'USD', 'SGD'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, currency: opt }))}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition',
                      draft.currency === opt
                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                    )}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('services.fields.commissionPercentage')}
              type="number"
              min="0"
              max="100"
              step="any"
              value={draft.commissionPercentage}
              onChange={(e) => setDraft((d) => ({ ...d, commissionPercentage: e.target.value }))}
              placeholder={t('services.fields.commissionPlaceholder')}
            />
            <TextField
              label={t('services.fields.durationMinutes')}
              type="number"
              min="0"
              value={draft.unitValue}
              onChange={(e) => setDraft((d) => ({ ...d, unitValue: e.target.value }))}
              placeholder={t('services.fields.durationPlaceholder')}
            />
          </div>

          {/* ── Description & Notes ── */}
          <SectionLabel>{t('services.sections.description')}</SectionLabel>

          <RichTextEditor
            label={t('common.description')}
            value={draft.description}
            onChange={(val) => setDraft((d) => ({ ...d, description: val }))}
            placeholder={t('services.fields.descriptionPlaceholder')}
          />

          <TextField
            label={t('services.fields.remark')}
            value={draft.remark}
            onChange={(e) => setDraft((d) => ({ ...d, remark: e.target.value }))}
            placeholder={t('services.fields.remarkPlaceholder')}
          />

          {/* ── Staff & Availability ── */}
          <SectionLabel>{t('services.sections.staffAvailability')}</SectionLabel>

          {attendedByUsers.length > 0 && (
            <div>
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('services.fields.allowedStaff')}</div>
              <div className="flex flex-wrap gap-2">
                {attendedByUsers.map((u) => {
                  const selected = draft.allowAttendedBy.includes(u.userId);
                  return (
                    <button
                      key={u.userId}
                      type="button"
                      onClick={() => toggleAttendedBy(u.userId)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs font-medium transition',
                        selected
                          ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600'
                      )}
                    >
                      {u.name}
                    </button>
                  );
                })}
              </div>
              {draft.allowAttendedBy.length === 0 && (
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{t('services.fields.noStaffSelected')}</p>
              )}
            </div>
          )}

          <label className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 dark:border-slate-600"
              checked={draft.allowExternalBooking}
              onChange={(e) => setDraft((d) => ({ ...d, allowExternalBooking: e.target.checked }))}
            />
            <div>
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('services.fields.allowExternalBooking')}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{t('services.fields.allowExternalBookingHint')}</div>
            </div>
          </label>

          {/* ── Validity Period ── */}
          <SectionLabel>{t('services.sections.validityPeriod')}</SectionLabel>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('services.fields.validFrom')}
              type="date"
              value={draft.validFrom}
              onChange={(e) => setDraft((d) => ({ ...d, validFrom: e.target.value }))}
            />
            <TextField
              label={t('services.fields.validTo')}
              type="date"
              value={draft.validTo}
              onChange={(e) => setDraft((d) => ({ ...d, validTo: e.target.value }))}
            />
          </div>

          {/* ── Subscription ── */}
          <SectionLabel>{t('services.sections.subscription')}</SectionLabel>

          <label className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 dark:border-slate-600"
              checked={draft.isSubscription}
              onChange={(e) => setDraft((d) => ({ ...d, isSubscription: e.target.checked }))}
            />
            <div>
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('services.fields.subscriptionService')}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{t('services.fields.subscriptionServiceHint')}</div>
            </div>
          </label>

          {draft.isSubscription && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                label={t('services.fields.usageLimit')}
                type="number"
                min="1"
                value={draft.subscriptionLimit}
                onChange={(e) => setDraft((d) => ({ ...d, subscriptionLimit: e.target.value }))}
                placeholder={t('services.fields.usageLimitPlaceholder')}
              />
              <TextField
                label={t('services.fields.expiryDays')}
                type="number"
                min="1"
                value={draft.subscriptionDuration}
                onChange={(e) => setDraft((d) => ({ ...d, subscriptionDuration: e.target.value }))}
                placeholder={t('services.fields.expiryDaysPlaceholder')}
              />
            </div>
          )}

          {/* ── Actions ── */}
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 [&>button]:flex-1 sm:[&>button]:flex-none dark:border-slate-800">
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={saveDraft} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
